import {Highlighter, React, useAsObservableSource, useLocalStore, observer, useEffect} from '@api'
import {Icon} from '@blueprintjs/core'
import {useDetailsSecondariesStore} from '@design-board/stores'
import {CatalogItemChangeHistory, TableRegionView, CatalogFieldRenderer} from '@lazy'
import {CatalogItemModalDetailTabContentsStore, CatalogItemSidebarDetailsStore, useCatalogItemModalDetailTabContentsStore, useCatalogItemSidebarDetailsStore, useVipLocalStore} from '@modules'
import {computedFn} from 'mobx-utils'
import {Fragment, useContext} from 'react'
import * as css from './ModalDetailSection.css'
import {DetailsViewFieldConfig, DragDropEventHandlers, DetailsSectionConfig, ModalSectionRows, StoresInstance} from '@stores'
import {CollapsibleStateful, DragEventPlaceholders, PrimaryIconButton} from 'centric-ui-components'
import {action, computed, observable, reaction, trace} from 'mobx'
import {VipLocalStore} from '@modules'

interface MyProps {
  section: DetailsSectionConfig,
  tabId: string,
  index: number
  headerExtra?: React.ReactNode
}

export const ModalDetailSectionStoreContext = React.createContext<ModalDetailSectionStore>(undefined)
export const useModalDetailSectionStore = () => useContext(ModalDetailSectionStoreContext)

class ModalDetailSectionStore extends VipLocalStore {
  constructor(stores: StoresInstance,public props: MyProps & { detailsStore: CatalogItemSidebarDetailsStore, tabStore?: CatalogItemModalDetailTabContentsStore, section?: DetailsSectionConfig}) {
    // public , public
    super(stores)

    const {section: {defaultCollapsed}, detailsStore: {sidebar: {sectionState}}} = props,
      {id} = this
    this.open = _.isBoolean(sectionState[id]?.open) ? sectionState[id].open : !defaultCollapsed
  }

  get detailsStore() { return this.props.detailsStore}
  get section() { return this.props.section}

  registerEffects = () => {
    const toDispose = [
      reaction(() => this.open, () => {
        const {id, detailsStore: {sidebar: {sectionState}}} = this
        sectionState[id] = {...(sectionState[id] ?? {}), open: this.open}
      })
    ]
    return () => toDispose.forEach(f => f())
  }

  @computed get id() {
    const {props: {section}, detailsStore: {selectedDetailTab}} = this
    return `${selectedDetailTab}.${section.id}`
  }

  @observable open = true
  @action toggleExpanded = () => this.open = !this.open

  @computed get dragDropEventHandlers(): DragDropEventHandlers {
    const {section: {dropTarget}, detailsStore: {id}, catalog} = this

    return !dropTarget ? null : new DragDropEventHandlers(catalog, {id, target: dropTarget.field})
  }

  get rows(): ModalSectionRows {
    const {section} = this

    if (!section.rows) return {}

    return section?.rows
  }

  get fields(): Array<DetailsViewFieldConfig> {
    const {catalog, section, detailsStore: {data}} = this

    if (section.fields) return _.isArray(section.fields) ? section.fields : section.fields(data, this.stores)

    if (section.field) {
      const {field: {field}} = section,
        //catalogField = catalog._fieldByName[field],
        library = catalog.libraries.find(l => l.catalogFields?.includes(field) || l.assignTo == field)

      if (!library) {
        console.log(`Unable to find a library with assignTo or catalogFields equal to ${field}`)
        return []
      }
      catalog.dataMapForLibrary(library)
      const workspace = catalog.workspaceByIndex[library.index]

      return [
        // {
        //   field: field,
        //   fieldRenderer: 'card',
        // },
        {
          field: field,
          titleField: 'name',
          fields: [
            ...(workspace?.catalog?.fields?.filter(f => f?.card?.default) || []).map(({field}) => ({field}))
          ]
        }
      ]
    }

    return []
  }

  @computed get sectionField() {
    const {catalog, section} = this

    if (!section.field) return null

    const {section: {field: {field}}} = this,
      //catalogField = catalog._fieldByName[field],
      library = catalog.libraries.find(l => l.catalogFields?.includes(field) || l.assignTo == field)

    if (!library) {
      console.log(`Unable to find a library with assignTo or catalogFields equal to ${field}`)
      return null
    }
    const workspace = catalog.workspaceByIndex[library.index],
      dataMap = catalog.dataMapForLibrary(library)

    return {library, workspace, dataMap}
  }

  get tabStore() { return this.props.tabStore }
  @computed get columns() {
    const {fields, props: {section}, tabStore} = this
    return Math.min(fields?.length, tabStore?.responsiveColumns ?? section?.columns ?? 4)
  }

  gridColumnAtIndex = computedFn((index: number) => {
    const {columns, fields, gridColumnAtIndex} = this,
      f = fields[index],
      previousIndexColumn = index === 0 ? null : gridColumnAtIndex(index - 1),
      start = previousIndexColumn ? ((previousIndexColumn.start + previousIndexColumn.colSpan - 1) % columns) + 1 : 1,
      colSpan = Math.min(columns, f?.colSpan ?? 1)

      return {
        start,
        colSpan: (start + colSpan) > columns
                 ? start + colSpan - columns
                 : colSpan
      }
  })

  styleForFieldIndex = computedFn((index: number) => {
    const {gridColumnAtIndex} = this,
      {colSpan, start} = gridColumnAtIndex(index)

    return _.pickBy({
      gridColumn: `${start} / span ${colSpan}`
    })
  })

  @computed get finalRowColumns() {
    const {fields, columns} = this
    return fields?.length % columns
  }

  @computed get fillColumns() {
    const {finalRowColumns, columns} = this
    return finalRowColumns ? columns - finalRowColumns : 0
  }

  @computed get isSearchResult() {
    const {props: {index, tabId}, detailsStore, detailsStore: {isSearching}} = this
    return isSearching && detailsStore.searchResults.some(sr => sr.tab == tabId && sr.type == 'section' && sr.index == index)
  }

  @computed get isCurrentSearchResult() {
    const {isSearchResult, props: {index, tabId}, detailsStore: {currentSearchResult}} = this

    return isSearchResult && currentSearchResult.tab == tabId && currentSearchResult.type == 'section' && currentSearchResult.index == index
  }

  @computed get count() {
    const {detailsStore: {data}, es, props: {section: {countField}}} = this
    return data && countField && es.readField(data, countField)?.val?.length
  }
}

const ModalSectionHeader = observer(function ModalSectionHeader(){
  const {detailsStore: {id, sidebar: {searchWords}}, props: {section: {label}, headerExtra, section}, open, toggleExpanded, dragDropEventHandlers, count} = useModalDetailSectionStore()
  return label ?
         <div className={css.sectionTitlebar} onDoubleClick={toggleExpanded}>
           <PrimaryIconButton icon={<Icon icon={open ? 'chevron-up' : 'chevron-down'}/>} onClick={toggleExpanded}/>
           {!section.field ?
            <Highlighter className={css.sectionLabel} textToHighlight={label} searchWords={searchWords}/> :
            <div className={css.sectionLabel}>
              <label>{label}:</label>
            </div>}
           {count && <div className={css.count}>({count.toLocaleString()})</div>}
           {dragDropEventHandlers && <DragEventPlaceholders id={id} dropType={dragDropEventHandlers.def.target}/>}
           {headerExtra}
         </div> : null
})

const ModalSectionContents = observer(function ModalSectionContents() {
  const {props: {section, tabId}, fields, rows, columns, fillColumns, catalog, styleForFieldIndex} = useModalDetailSectionStore(),
    {settings: {detailLoc: align}} = catalog

  return <div className={css.fieldsContainer} data-type='ModalSectionContents' data-columns={columns}>
    {section.type === 'changelog'
     ? <CatalogItemChangeHistory section={section}/>
     : <div className={css.fieldsGrid} >
       {(fields ?? []).map((f, j) => (
         <CatalogFieldRenderer
           data-align-type={align}
           tabId={tabId}
           section={section}
           className={css.field}
           align='modal'
           key={f.field ?? j}
           field={f}
           style={styleForFieldIndex(j)}
         />))}
       {!_.isEmpty(rows) && <TableRegionView rows={rows} hasFields={!!fields.length}/>}
       {!section.noFill && _.range(0, fillColumns).map(c => <div className={css.gridFiller} key={c}/>)}
     </div>}
  </div>
})

export const ModalDetailSection = observer<MyProps>(function ModalDetailSection(props) {
  const detailsStore = useCatalogItemSidebarDetailsStore(), secondaries = useDetailsSecondariesStore(),
    tabStore = useCatalogItemModalDetailTabContentsStore(),
    obsProps = useAsObservableSource({...props, secondaries, detailsStore, tabStore}),
    store = useVipLocalStore((ws) => new ModalDetailSectionStore(ws, obsProps)),
    {section, section: {label},
      open, dragDropEventHandlers, columns, isCurrentSearchResult, isSearchResult,toggleExpanded, catalog: {settings: {detailLoc: align}},
      detailsStore: {isSearching}} = store

  useEffect(() => store.registerEffects(), [])

  return (
    <ModalDetailSectionStoreContext.Provider value={store}>
      <div className={css.root}
           data-section={section.label}
           style={{
             '--grid-columns': align === 'modal' ? columns : 1,
             '--grid-gap': align === 'modal' ? '1px' : '0px',
             '--grid-background': align !== 'modal' ? 'var(--white)' : 'var(--background-grey-5)',
             ...(section.style ?? {})
           } as any}
           data-expanded={open || isCurrentSearchResult || align !== 'modal'}
           data-columns={columns}
           data-is-search-result={isSearching ? isSearchResult : undefined}
           data-is-current-search-result={isSearching ? isCurrentSearchResult : undefined}
           {...(dragDropEventHandlers?.events ?? {})}
      >
        {align === 'modal' ? <Fragment>
          <ModalSectionHeader/>
          <ModalSectionContents/>
        </Fragment> : <CollapsibleStateful data-sidebar-type={align} className={css.collapsible} classes={{list: css.list, wrapperInner: css.wrapperInner}} title={label}
         open={!open} onToggleOpen={() => toggleExpanded()}>
           <ModalSectionContents/>
         </CollapsibleStateful>}
      </div>
    </ModalDetailSectionStoreContext.Provider>
  )
})
ModalDetailSection['whyDidYouRender'] = true


// WEBPACK FOOTER //
// ./src/modules/CatalogItemDetails/CatalogItemSidebarDetails/Modal/ModalDetailSection.tsx